// Hook managing selection and URL routing.
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

/**
 * Manages task selection state and URL-driven navigation for the OrganizePage.
 * Handles:
 *   - Syncing the selected task from the URL param on mount/change
 *   - Updating the URL when selection changes
 *   - Opening the BulkEditModal via the Enter key when multiple tasks are selected
 *   - Deriving the single selected task object from allTasks
 */
export function useOrganizeNavigation(allTasks, loading) {
  const { taskId: taskIdFromUrl } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);

  // Sync URL param → selection state (never clobber an active multi-select).
  useEffect(() => {
    if (!taskIdFromUrl) return;
    setSelectedTaskIds((prev) => {
      if (prev.length > 1 && prev.includes(taskIdFromUrl)) return prev;
      if (prev.length === 1 && prev[0] === taskIdFromUrl) return prev;
      return [taskIdFromUrl];
    });
  }, [taskIdFromUrl]);

  // Open BulkEditModal via Enter key when multiple tasks are selected
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key !== 'Enter' || selectedTaskIds.length <= 1) return;
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA')
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsBulkEditModalOpen(true);
    };

    document.addEventListener('keydown', handleGlobalKeyDown, {
      capture: true,
    });
    return () =>
      document.removeEventListener('keydown', handleGlobalKeyDown, {
        capture: true,
      });
  }, [selectedTaskIds]);

  const handleSelectionChange = (newSelectedIds) => {
    setSelectedTaskIds(newSelectedIds);
    // Preserve query params (e.g. ?tab=backlog) so tab state survives selection URLs.
    const search = location.search || '';
    // Alleen single-select deeplink in de URL zetten. Bij multi-select navigatie
    // naar /:taskId triggert de URL-effect en wist de selectie terug naar 1.
    if (newSelectedIds.length === 1) {
      navigate(`/app/organize/${newSelectedIds[0]}${search}`, {
        replace: true,
      });
    } else if (newSelectedIds.length === 0) {
      navigate(`/app/organize${search}`, { replace: true });
    }
  };

  const handleCloseDetail = () => handleSelectionChange([]);

  // Derive the single selected root task (null when loading, multi-select, or a subtask)
  const selectedTask = useMemo(() => {
    if (loading || !allTasks || selectedTaskIds.length !== 1) return null;
    const selected = allTasks.find((t) => t.id === selectedTaskIds[0]);
    if (selected && selected.parentId) return null;
    return selected;
  }, [selectedTaskIds, allTasks, loading]);

  return {
    selectedTaskIds,
    selectedTask,
    isBulkEditModalOpen,
    setIsBulkEditModalOpen,
    handleSelectionChange,
    handleCloseDetail,
  };
}
